import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../hooks/useCompany';
import { employeesAPI, subsidiariesAPI, usersAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Users,
  Mail,
  Phone,
  Briefcase,
  Calendar,
  DollarSign,
  Building2,
  ChevronRight,
  ChevronLeft,
  Check,
  X,
  Edit2,
  User,
  Clock,
  BadgeDollarSign,
  UserCheck,
} from 'lucide-react';

const formatCurrency = (value, currency = 'USD') => {
  if (currency === 'LRD') {
    return `L$${(value || 0).toLocaleString()}`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(value || 0);
};

// Employee Creation Wizard
function AddEmployeeWizard({ isOpen, onClose, subsidiaries, companyId, users, existingEmployees }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [formData, setFormData] = useState({
    // Step 1: Select User or Enter Basic Identity
    user_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    subsidiary_id: '',
    job_title: '',
    status: 'active',
    hire_date: new Date().toISOString().split('T')[0],
    // Step 3: Payroll Profile
    pay_type: 'monthly',
    monthly_salary: '',
    hourly_rate: '',
    expected_hours: 160,
    currency: 'USD',
    is_taxable: true,
    allowances: '',
  });

  // Get users who don't have employee profiles yet
  const existingUserIds = existingEmployees?.map(e => e.user_id).filter(Boolean) || [];
  const availableUsers = users?.filter(u => 
    u.status === 'active' && !existingUserIds.includes(u.id)
  ) || [];

  const createMutation = useMutation({
    mutationFn: (data) => employeesAPI.create({ ...data, company_id: companyId }),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['employees']);
      toast.success(`Employee ${response.data.employee_number} created successfully!`);
      onClose();
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create employee');
    },
  });

  const resetForm = () => {
    setStep(1);
    setSelectedUserId('');
    setFormData({
      user_id: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      subsidiary_id: '',
      job_title: '',
      status: 'active',
      hire_date: new Date().toISOString().split('T')[0],
      pay_type: 'monthly',
      monthly_salary: '',
      hourly_rate: '',
      expected_hours: 160,
      currency: 'USD',
      is_taxable: true,
      allowances: '',
    });
  };

  const handleNext = () => {
    if (step === 1) {
      if (!selectedUserId || !formData.subsidiary_id || !formData.job_title) {
        toast.error('Please select a user and fill all required fields');
        return;
      }
    }
    setStep(step + 1);
  };

  const handleSubmit = () => {
    const submitData = {
      ...formData,
      monthly_salary: parseFloat(formData.monthly_salary) || 0,
      hourly_rate: parseFloat(formData.hourly_rate) || 0,
      allowances: parseFloat(formData.allowances) || 0,
    };
    createMutation.mutate(submitData);
  };

  const selectedSub = subsidiaries.find(s => s.id === formData.subsidiary_id);
  const previewCode = selectedSub ? `GR${selectedSub.code?.substring(0, 3).toUpperCase() || 'GEN'}XXX` : 'GRXXXYYY';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Add New Employee</h2>
              <p className="text-primary-foreground/80 text-sm">Step {step} of 4</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition">
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* Progress Bar */}
          <div className="flex gap-2 mt-4">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`flex-1 h-2 rounded-full transition-all ${
                  s <= step ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Step 1: Select User */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <UserCheck className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Select User Account</h3>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Important:</strong> Users must be created in <strong>Settings → User Management</strong> first, 
                  then HR can set up their employee profile here.
                </p>
              </div>

              {availableUsers.length === 0 ? (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-center">
                  <p className="text-amber-800 dark:text-amber-200">
                    No available users without employee profiles.
                  </p>
                  <p className="text-sm text-amber-600 dark:text-amber-300 mt-1">
                    Create new users in Settings → User Management first.
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-2">Select a User *</label>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {availableUsers.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => {
                          setSelectedUserId(user.id);
                          setFormData({
                            ...formData,
                            user_id: user.id,
                            first_name: user.first_name,
                            last_name: user.last_name,
                            email: user.email,
                          });
                        }}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                          selectedUserId === user.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary">
                            {user.first_name?.[0]}{user.last_name?.[0]}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{user.first_name} {user.last_name}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                          user.role === 'manager' ? 'bg-green-100 text-green-700' :
                          user.role === 'accountant' ? 'bg-blue-100 text-blue-700' :
                          user.role === 'teller' ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {user.role}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selectedUserId && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Subsidiary *</label>
                      <select
                        value={formData.subsidiary_id}
                        onChange={(e) => setFormData({ ...formData, subsidiary_id: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                        data-testid="select-subsidiary"
                      >
                        <option value="">Select Subsidiary</option>
                        {subsidiaries.map((sub) => (
                          <option key={sub.id} value={sub.id}>
                            {sub.name} ({sub.code})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Job Title *</label>
                      <input
                        type="text"
                        value={formData.job_title}
                        onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                        placeholder="Software Engineer"
                        data-testid="input-job-title"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Hire Date</label>
                      <input
                        type="date"
                        value={formData.hire_date}
                        onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                        data-testid="input-hire-date"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Phone (Optional)</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                        placeholder="+231 XXX XXX XXX"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Employee Code Preview */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <BadgeDollarSign className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Employee Code Generation</h3>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Code Format</h4>
                <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
                  Employee codes are auto-generated using: <strong>CompanyPrefix + SubsidiaryCode + Sequence</strong>
                </p>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                  <p className="text-sm text-muted-foreground mb-2">Your employee code will be:</p>
                  <div className="text-3xl font-mono font-bold text-primary">
                    {previewCode}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    (XXX will be replaced with the next sequence number)
                  </p>
                </div>
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>Note:</strong> Once payroll has been run, the employee code cannot be changed.
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Payroll Profile */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Payroll Profile</h3>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-800 dark:text-green-200">
                  This information determines how the employee will be paid each pay period.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Pay Type *</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, pay_type: 'monthly' })}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.pay_type === 'monthly'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid="btn-pay-monthly"
                  >
                    <Calendar className="h-6 w-6 mb-2 text-primary" />
                    <p className="font-medium">Monthly Salary</p>
                    <p className="text-xs text-muted-foreground">Fixed monthly amount</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, pay_type: 'hourly' })}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.pay_type === 'hourly'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid="btn-pay-hourly"
                  >
                    <Clock className="h-6 w-6 mb-2 text-primary" />
                    <p className="font-medium">Hourly Rate</p>
                    <p className="text-xs text-muted-foreground">Based on hours worked</p>
                  </button>
                </div>
              </div>

              {formData.pay_type === 'monthly' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Monthly Salary *</label>
                    <input
                      type="number"
                      value={formData.monthly_salary}
                      onChange={(e) => setFormData({ ...formData, monthly_salary: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                      placeholder="5000"
                      data-testid="input-monthly-salary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                      data-testid="select-currency"
                    >
                      <option value="USD">USD ($)</option>
                      <option value="LRD">LRD (L$)</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Hourly Rate *</label>
                    <input
                      type="number"
                      value={formData.hourly_rate}
                      onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                      placeholder="25"
                      data-testid="input-hourly-rate"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Expected Hours/Month</label>
                    <input
                      type="number"
                      value={formData.expected_hours}
                      onChange={(e) => setFormData({ ...formData, expected_hours: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                      placeholder="160"
                      data-testid="input-expected-hours"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                      data-testid="select-currency-hourly"
                    >
                      <option value="USD">USD ($)</option>
                      <option value="LRD">LRD (L$)</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Allowances (Optional)</label>
                  <input
                    type="number"
                    value={formData.allowances}
                    onChange={(e) => setFormData({ ...formData, allowances: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                    placeholder="0"
                    data-testid="input-allowances"
                  />
                </div>
                <div className="flex items-center pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_taxable}
                      onChange={(e) => setFormData({ ...formData, is_taxable: e.target.checked })}
                      className="w-4 h-4 rounded border-border"
                      data-testid="checkbox-taxable"
                    />
                    <span className="text-sm">Subject to Income Tax</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review & Confirm */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Check className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Review & Confirm</h3>
              </div>

              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Name</span>
                  <span className="font-medium">{formData.first_name} {formData.last_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span className="font-medium">{formData.email || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subsidiary</span>
                  <span className="font-medium">{selectedSub?.name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Job Title</span>
                  <span className="font-medium">{formData.job_title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Employee Code</span>
                  <span className="font-mono font-bold text-primary">{previewCode}</span>
                </div>
                <hr className="border-border" />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pay Type</span>
                  <span className="font-medium capitalize">{formData.pay_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {formData.pay_type === 'monthly' ? 'Monthly Salary' : 'Hourly Rate'}
                  </span>
                  <span className="font-medium">
                    {formData.pay_type === 'monthly'
                      ? formatCurrency(formData.monthly_salary, formData.currency)
                      : `${formatCurrency(formData.hourly_rate, formData.currency)}/hr`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Taxable</span>
                  <span className="font-medium">{formData.is_taxable ? 'Yes' : 'No'}</span>
                </div>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <p className="text-sm text-green-800 dark:text-green-200">
                  Upon creation, the employee will receive an email notification about their profile.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4 flex justify-between bg-muted/30">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="flex items-center gap-2 px-4 py-2 text-muted-foreground hover:text-foreground transition"
          >
            <ChevronLeft className="h-4 w-4" />
            {step > 1 ? 'Back' : 'Cancel'}
          </button>
          
          {step < 4 ? (
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition"
              data-testid="btn-next-step"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
              data-testid="btn-create-employee"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Employee'}
              <Check className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Main Employees Page
export default function Employees() {
  const { selectedCompany } = useCompany();
  const [showWizard, setShowWizard] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const { data: employees = [], isLoading } = useQuery({
    queryKey: ['employees', selectedCompany?.id],
    queryFn: () => employeesAPI.getAll(selectedCompany?.id).then((res) => res.data),
    enabled: !!selectedCompany?.id,
  });

  const { data: subsidiaries = [] } = useQuery({
    queryKey: ['subsidiaries', selectedCompany?.id],
    queryFn: () => subsidiariesAPI.getAll(selectedCompany?.id).then((res) => res.data),
    enabled: !!selectedCompany?.id,
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users', selectedCompany?.id],
    queryFn: () => usersAPI.getAll(selectedCompany?.id).then((res) => res.data),
    enabled: !!selectedCompany?.id,
  });

  const filteredEmployees = employees.filter((emp) => {
    const matchesSearch =
      emp.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.employee_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSub = !filterDept || emp.subsidiary_id === filterDept;
    const matchesStatus = !filterStatus || emp.status === filterStatus;
    
    return matchesSearch && matchesSub && matchesStatus;
  });

  // Stats
  const activeCount = employees.filter(e => e.status === 'active').length;
  const totalPayroll = employees.reduce((sum, e) => {
    if (e.currency === 'USD') {
      return sum + (e.monthly_salary || e.salary || 0);
    }
    return sum;
  }, 0);

  return (
    <div className="space-y-6" data-testid="employees-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Employees</h1>
          <p className="text-muted-foreground">Manage your workforce and payroll profiles</p>
        </div>
        <button
          onClick={() => setShowWizard(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition"
          data-testid="btn-add-employee"
        >
          <Plus className="h-4 w-4" />
          Add Employee
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{employees.length}</p>
              <p className="text-xs text-muted-foreground">Total Employees</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Check className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{activeCount}</p>
              <p className="text-xs text-muted-foreground">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Building2 className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{subsidiaries.length}</p>
              <p className="text-xs text-muted-foreground">Subsidiaries</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <DollarSign className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{formatCurrency(totalPayroll)}</p>
              <p className="text-xs text-muted-foreground">Monthly Payroll (USD)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name, code, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background"
            data-testid="search-employees"
          />
        </div>
        <select
          value={filterDept}
          onChange={(e) => setFilterDept(e.target.value)}
          className="px-4 py-2 border border-border rounded-lg bg-background"
          data-testid="filter-subsidiary"
        >
          <option value="">All Subsidiaries</option>
          {subsidiaries.map((sub) => (
            <option key={sub.id} value={sub.id}>{sub.name}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-border rounded-lg bg-background"
          data-testid="filter-status"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="on_hold">On Hold</option>
          <option value="terminated">Terminated</option>
        </select>
      </div>

      {/* Employee Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium">Employee</th>
              <th className="text-left px-4 py-3 text-sm font-medium">Code</th>
              <th className="text-left px-4 py-3 text-sm font-medium">Subsidiary</th>
              <th className="text-left px-4 py-3 text-sm font-medium">Job Title</th>
              <th className="text-left px-4 py-3 text-sm font-medium">Pay Type</th>
              <th className="text-right px-4 py-3 text-sm font-medium">Salary</th>
              <th className="text-center px-4 py-3 text-sm font-medium">Status</th>
              <th className="text-center px-4 py-3 text-sm font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                  Loading employees...
                </td>
              </tr>
            ) : filteredEmployees.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                  {employees.length === 0 ? 'No employees yet. Click "Add Employee" to get started.' : 'No employees match your search.'}
                </td>
              </tr>
            ) : (
              filteredEmployees.map((emp) => (
                <tr key={emp.id} className="hover:bg-muted/30 transition">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-medium text-primary">
                          {emp.first_name?.[0]}{emp.last_name?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{emp.first_name} {emp.last_name}</p>
                        <p className="text-xs text-muted-foreground">{emp.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
                      {emp.employee_number}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{emp.subsidiary_name || '-'}</td>
                  <td className="px-4 py-3 text-sm">{emp.job_title || emp.position || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      emp.pay_type === 'hourly' 
                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    }`}>
                      {emp.pay_type === 'hourly' ? 'Hourly' : 'Monthly'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {emp.pay_type === 'hourly'
                      ? `${formatCurrency(emp.hourly_rate, emp.currency)}/hr`
                      : formatCurrency(emp.monthly_salary || emp.salary, emp.currency)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      emp.status === 'active'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        : emp.status === 'on_hold'
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                      {emp.status === 'active' ? 'Active' : emp.status === 'on_hold' ? 'On Hold' : 'Terminated'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button className="p-1 hover:bg-muted rounded transition" data-testid={`edit-employee-${emp.id}`}>
                      <Edit2 className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Employee Wizard */}
      <AddEmployeeWizard
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        subsidiaries={subsidiaries}
        companyId={selectedCompany?.id}
        users={users}
        existingEmployees={employees}
      />
    </div>
  );
}
